import { inject, Injector, runInInjectionContext } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { defer, EMPTY, filter, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { UnionToIntersection } from '@recruitee/common';
import { generateUuid } from '@recruitee/utils';

import { ConfigActionType, HttpActionNames } from './types';

export const unwrap = <T extends { success: any; failed: any }>(handler: {
  success: (value: Exclude<T['success'], undefined>) => void;
  failed: (value: Exclude<T['failed'], undefined>) => void;
}) => ({
  next: (result: T) => {
    result.failed ? handler.failed(result.failed) : handler.success(result.success);
  },
});

/* Action tracking idea */
const uuidKey = Symbol('actions track');
type ActionWithUuid = Action & { [uuidKey]: string };
const setActionID = (action: Action, uuid: string): ActionWithUuid => ({
  [uuidKey]: uuid,
  ...action,
});
export const getActionID = (action: Action): string | null => (<ActionWithUuid>action)[uuidKey];

export const injectTraceableActions = (): {
  track: <AC extends ActionCreator[]>(
    actions: AC,
    mapFn: typeof switchMap | typeof mergeMap,
    actions$: (
      arg: ReturnType<AC extends Array<ActionCreator<any, infer C>> ? C : never>,
    ) => Observable<any>,
  ) => Observable<any>;
} => {
  const actions$ = inject(Actions);
  return {
    track: <AC extends ActionCreator[]>(
      trackable: AC,
      mapFn: typeof switchMap,
      pipeFn: (
        arg: ReturnType<AC extends Array<ActionCreator<any, infer C>> ? C : never>,
      ) => Observable<any>,
    ) =>
      actions$.pipe(
        ofType(...trackable),
        mapFn((action: any) => {
          const uuid = getActionID(action);
          return of(action).pipe(
            switchMap(action => pipeFn(action)),
            map(resultAction => {
              if (uuid) return setActionID(resultAction, uuid);

              console.log('Cant track', action, resultAction);
              return resultAction;
            }),
          );
        }),
      ),
  };
};

type ActionsWithSuccessOrFail<
  AllActions extends Record<string, ConfigActionType<any, any>>,
  Action = Extract<keyof AllActions, string>,
> = Action extends string
  ? AllActions extends Record<HttpActionNames<Action>, any>
    ? Action
    : never
  : never;

type UnwrapSelector<T extends (...args: any[]) => Observable<any>> =
  ReturnType<T> extends Observable<infer R> ? R : never;

type Handlers = {
  success: (arg: any) => Observable<any>;
  failed?: (arg: any) => Observable<any>;
};

type ActionConnector<
  Action extends string,
  AllActions extends Record<string, ConfigActionType<any, any>>,
> = UnionToIntersection<
  Action extends string
    ? {
        [k in Action]: <
          SuccessSelector extends (arg: ReturnType<AllActions[`${k}Success`]>) => Observable<any>,
          FailedSelector extends (arg: ReturnType<AllActions[`${k}Failed`]>) => Observable<unknown>,
        >(
          payload: Parameters<AllActions[k]>[0] extends { payload: infer R } ? R : void,
          handlers: {
            success: SuccessSelector;
            failed?: FailedSelector;
          },
        ) => Observable<
          | {
              success: UnwrapSelector<SuccessSelector>;
              failed: undefined;
            }
          | {
              success: undefined;
              failed: UnwrapSelector<FailedSelector>;
            }
        >;
      }
    : never
>;

export const createActionResponseFactory2 = <
  TypedActions extends { [k: string]: ConfigActionType<any, any> },
>(
  actions: TypedActions,
): ActionConnector<ActionsWithSuccessOrFail<TypedActions>, TypedActions> => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const injector = inject(Injector);
  return <any>new Proxy(
    {},
    {
      get: (_, prop: ActionsWithSuccessOrFail<TypedActions>) => {
        const actionCreator = actions[prop];
        const actionCreatorSuccess = actions[`${prop}Success`];
        const actionCreatorFailed = actions[`${prop}Failed`];
        return (payload: any, handlers: Handlers) =>
          defer(() => {
            const uuid = generateUuid();
            const action = setActionID(actionCreator(payload), uuid);
            store.dispatch(action);
            return actions$.pipe(
              ofType(actionCreatorSuccess, actionCreatorFailed),
              filter(action => getActionID(action) === uuid),
              switchMap((action: Action) =>
                runInInjectionContext(injector, () => {
                  if (action.type === actionCreatorSuccess.type) {
                    return handlers
                      .success(action)
                      .pipe(map(success => ({ success, failed: undefined })));
                  }
                  if (action.type === actionCreatorFailed.type) {
                    return (
                      handlers
                        .failed?.(action)
                        .pipe(map(failed => ({ success: undefined, failed }))) ?? EMPTY
                    );
                  }

                  return throwError('');
                }),
              ),
              take(1),
            );
          });
      },
    },
  );
};
